import { ChangeEvent, FormEvent, useState } from "react";
import { PdfGuide } from "./PdfGuide";
import { submitOrgInterest } from "./services";
import { validateEmail } from "./utils";

interface FormState {
  complex: string;
  dayTime: string;
  space: string;
  leading: string;
  name: string;
  email: string;
  other: string;
}

const initialForm: FormState = {
  complex: "",
  dayTime: "",
  space: "",
  leading: "",
  name: "",
  email: "",
  other: "",
};

export function Org(): JSX.Element {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await submitOrgInterest(form);
      if (!response.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="org-join">
        <h3>Thanks!</h3>
        <p>
          We got your info. Someone will be in touch about getting your tenant
          union started.
        </p>
      </div>
    );
  }

  const canSubmit: boolean = Boolean(
    form.complex.trim() &&
    form.name.trim() &&
    form.email.trim() &&
    !submitting &&
    validateEmail(form.email),
  );

  return (
    <>
      <PdfGuide
        title='Read the "HOW TO ORGANIZE A TENANTS UNION" pdf'
        pdfUrl="/how-to-form-a-tenants-union-2024-for-web.pdf"
      />
      <form className="org-join" onSubmit={handleSubmit}>
        <h1>Let's form a Tenant Union!</h1>

        <label className="small-text">What complex are you at?</label>
        <input
          type="text"
          name="complex"
          value={form.complex}
          onChange={handleChange}
          required
        />

        <label className="small-text">
          What would be a good day & time to meet with your neighbors?
        </label>
        <input
          type="text"
          name="dayTime"
          value={form.dayTime}
          onChange={handleChange}
          placeholder="e.g. Saturdays around 2pm"
        />

        <label className="small-text">
          What's a good space to meet in that's nearby or on premises?
        </label>
        <input
          type="text"
          name="space"
          value={form.space}
          onChange={handleChange}
          placeholder="e.g. courtyard, lobby, nearby cafe"
        />

        <label className="small-text">
          Are you interested in leading the discussion?
        </label>
        <input
          type="text"
          name="leading"
          value={form.leading}
          onChange={handleChange}
          placeholder="Yes / No / Maybe"
        />

        <label className="small-text">What's your name? (can be fake)</label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
        />

        <label className="small-text">What's your email?</label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          required
        />

        <label className="small-text">Anything else you want to share?</label>
        <textarea
          name="other"
          value={form.other}
          onChange={handleChange}
          rows={4}
          className="textarea"
        />

        {error && (
          <p style={{ color: "#e42869", background: "transparent" }}>{error}</p>
        )}

        <button type="submit" disabled={!canSubmit}>
          {submitting ? "Sending..." : "Submit"}
        </button>
      </form>
    </>
  );
}
